import { EFinancingProgram } from '@if/core/types'
import { ControlledResourceAutocomplete, ResourceAutocompleteSearchResult } from '@if/mui'
import { useFindMerchantById, useSearchMerchantsForResource } from '@src/data/api/merchant/merchant-api'
import { Merchant } from '@src/data/types'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { Control, FieldPathByValue, FieldValues } from 'react-hook-form'

export interface MerchantAutocompleteProps<
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, string | null>,
> {
  control: Control<TFieldValues>
  name: TName
  financingProgramId: EFinancingProgram
  label?: string
  disabled?: boolean
}

const getMerchantId = (m: Merchant) => m.id
const getMerchantLabel = (m: Merchant) => m.name

const MerchantAutocomplete = <
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, string | null>,
>({
  control,
  name,
  financingProgramId,
  label,
  disabled,
}: MerchantAutocompleteProps<TFieldValues, TName>) => {
  const useSearch = (search: string): ResourceAutocompleteSearchResult<Merchant> => {
    const { selectedCompany } = usePreferences()
    const trimmed = search.trim()
    const dto = trimmed
      ? { financingProgramId, financingCompanyId: selectedCompany, nameContains: trimmed, limit: 25, isActive: true }
      : null
    return useSearchMerchantsForResource(dto)
  }
  return (
    <ControlledResourceAutocomplete<TFieldValues, Merchant, TName>
      control={control}
      name={name}
      label={label}
      disabled={disabled}
      useSearch={useSearch}
      useFindById={useFindMerchantById}
      getOptionId={getMerchantId as never}
      getOptionLabel={getMerchantLabel}
      fullWidth
    />
  )
}

export default MerchantAutocomplete
